// DEPLOY-PROBE-bump

/**
 * AddNewUserModal Component
 * 
 * Modal component for adding or editing office employees.
 * Provides a form interface with validation for employee ID, name, user type,
 * and password. Supports both add and edit modes with different validation rules.
 * 
 * Features:
 * - Add mode: Full validation including 4-digit employee code, Japanese name,
 *   required password with complexity rules
 * - Edit mode: Employee ID is disabled; name and user type are editable; password is optional
 * - Form validation with Japanese error messages
 * - Portal rendering to avoid parent container clipping
 * - Body scroll lock when modal is open
 * - Japanese language interface
 * 
 * Validation Rules:
 * - Employee code: Must be 4 digits (add mode only, like construction workers)
 * - Name: Must be Japanese characters (Kanji, Hiragana, Katakana)
 * - Password: Minimum 6 characters, must contain at least one uppercase letter
 *   and one digit, alphanumeric only
 * - Password confirmation: Must match password
 * 
 * Used by:
 * - UserManagementMain component for employee management
 * 
 * @component
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './AddNewUserModal.css';
import { fileUploadService } from '../../../../services/FileUploadService';
import { OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN } from './UnifiedRegisterModal';

/**
 * Props Interface for AddNewUserModal Component
 * 
 * @interface AddNewUserModalProps
 * @property {boolean} isOpen - Whether the modal is open
 * @property {() => void} onClose - Callback function to close the modal
 * @property {(userData: UserData) => void} [onAddUser] - Optional callback for adding new user
 * @property {(userData: UserData) => void} [onUpdateUser] - Optional callback for updating user
 * @property {'add' | 'edit'} [mode='add'] - Modal mode: 'add' for new user, 'edit' for existing user
 * @property {Partial<UserData>} [initialData] - Optional initial data for edit mode
 */
interface AddNewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser?: (userData: UserData) => void;
  onUpdateUser?: (userData: UserData) => void;
  mode?: 'add' | 'edit';
  initialData?: Partial<UserData>;
}

/**
 * User Data Interface
 * 
 * @interface UserData
 * @property {string} empId - Employee code (4 digits)
 * @property {string} name - Employee name (Japanese characters)
 * @property {string} userType - User type ('従業員' or '管理者')
 * @property {string} password - Password (minimum 6 characters with uppercase and digit)
 * @property {string} passwordConfirm - Password confirmation (must match password)
 * @property {string} [currentPassword] - In edit mode when changing password: current password so DEK is re-encrypted (optional).
 */
interface UserData {
  empId: string;
  name: string;
  userType: string;
  password: string;
  passwordConfirm: string;
  currentPassword?: string;
  /** Genba from construction salary data when userType is 担当現場管理者. */
  assignedGenbaName?: string;
}

/**
 * AddNewUserModal Component
 * 
 * Modal component for adding or editing office employees.
 * Manages form state, validation, and submission.
 * 
 * @param props - AddNewUserModalProps containing modal configuration and callbacks
 * @returns React functional component
 */
export const AddNewUserModal: React.FC<AddNewUserModalProps> = ({
  isOpen,
  onClose,
  onAddUser,
  onUpdateUser,
  mode = 'add',
  initialData
}) => {
  /**
   * State Management:
   * - formData: Current form data state (empId, name, userType, password, passwordConfirm)
   * - errors: Validation error messages for each field
   */
  const [formData, setFormData] = useState<UserData>({
    empId: '',
    name: '',
    userType: '従業員',
    password: '',
    passwordConfirm: '',
    currentPassword: '',
    assignedGenbaName: '',
  });

  const [availableGenbas, setAvailableGenbas] = useState<string[]>([]);
  const [genbasLoading, setGenbasLoading] = useState(false);
  const [genbasError, setGenbasError] = useState<string | null>(null);

  const [errors, setErrors] = useState<Partial<UserData>>({});

  /**
   * Initialize form data when modal opens or mode changes.
   * 
   * In edit mode, populates form with initialData and clears password fields.
   * In add mode, resets form to default empty values.
   * 
   * @effect
   * Runs when isOpen, mode, or initialData changes.
   */
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData((prev) => ({
          empId: initialData.empId ?? prev.empId,
          name: initialData.name ?? prev.name,
          userType: initialData.userType ?? prev.userType,
          password: '',
          passwordConfirm: '',
          currentPassword: '',
          assignedGenbaName: initialData.assignedGenbaName ?? '',
        }));
        setErrors({});
      } else if (mode === 'add') {
        setFormData({
          empId: '',
          name: '',
          userType: '従業員',
          password: '',
          passwordConfirm: '',
          currentPassword: '',
          assignedGenbaName: '',
        });
        setErrors({});
      }
    }
  }, [isOpen, mode, initialData]);

  useEffect(() => {
    if (!isOpen || formData.userType !== OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN) {
      return;
    }
    let cancelled = false;
    (async () => {
      setGenbasLoading(true);
      setGenbasError(null);
      try {
        const genbas = await fileUploadService.getConstructionAvailableGenbas();
        const list = (Array.isArray(genbas) ? genbas : [])
          .map((g) => String(g).trim())
          .filter(Boolean);
        const sorted = Array.from(new Set(list)).sort((a, b) => a.localeCompare(b, 'ja'));
        if (!cancelled) {
          setAvailableGenbas(sorted);
          setFormData((prev) => {
            const cur = prev.assignedGenbaName?.trim() ?? '';
            if (!cur) return prev;
            if (sorted.includes(cur)) return prev;
            return { ...prev, assignedGenbaName: '' };
          });
        }
      } catch {
        if (!cancelled) {
          setAvailableGenbas([]);
          setGenbasError('現場一覧の取得に失敗しました。');
        }
      } finally {
        if (!cancelled) setGenbasLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.userType]);

  /**
   * Validates if the value is a 4-digit employee code (same as construction workers).
   *
   * @param value - The value to validate
   * @returns {boolean} True if value is exactly 4 digits
   */
  const isFourDigitEmployeeCode = (value: string): boolean => {
    return /^\d{4}$/.test(value);
  };

  /**
   * Validates if the value contains only Japanese characters.
   * 
   * Accepts:
   * - Kanji (漢字): \u4E00-\u9FFF
   * - CJK Extension A: \u3400-\u4DBF
   * - Hiragana (ひらがな): \u3040-\u309F
   * - Katakana (カタカナ): \u30A0-\u30FF
   * - Katakana Phonetic Extension: \u31F0-\u31FF
   * - Common name marks: \u3000 (full-width space), \u3005 (々), \u30F5 (ヵ), \u30F6 (ヶ), \u30FB (・), \u30FC (ー)
   * - Whitespace
   * 
   * @param value - The value to validate
   * @returns {boolean} True if value contains only Japanese characters
   * 
   * @example
   * ```typescript
   * isJapaneseName('山田太郎'); // true
   * isJapaneseName('Tanaka'); // false
   * ```
   */
  const isJapaneseName = (value: string): boolean => {
    // Kanji: \u4E00-\u9FFF, CJK Ext A: \u3400-\u4DBF, Hiragana: \u3040-\u309F, Katakana: \u30A0-\u30FF, Katakana Phonetic Ext: \u31F0-\u31FF
    // Common name marks/spaces: \u3000 (full-width space), \u3005 (々), \u30F5 (ヵ), \u30F6 (ヶ), \u30FB (・), \u30FC (ー), whitespace
    const japaneseNameRegex = /^[\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\u4E00-\u9FFF\u3400-\u4DBF\u3000\u3005\u30F5\u30F6\u30FB\u30FC\s]+$/;
    return japaneseNameRegex.test(value);
  };

  /**
   * Validates if the password meets complexity requirements.
   * 
   * Requirements:
   * - At least one uppercase letter (A-Z)
   * - At least one digit (0-9)
   * - Only alphanumeric characters (letters and digits)
   * 
   * @param value - The password to validate
   * @returns {boolean} True if password meets all requirements
   * 
   * @example
   * ```typescript
   * isPasswordValid('Password123'); // true
   * isPasswordValid('password123'); // false (no uppercase)
   * ```
   */
  const isPasswordValid = (value: string): boolean => {
    // At least one uppercase and one digit; letters and digits only
    return /^(?=.*[A-Z])(?=.*\d)[A-Za-z0-9]+$/.test(value);
  };

  /**
   * Validates the entire form based on the current mode.
   * 
   * Validation rules differ between add and edit modes:
   * 
   * Add Mode:
   * - Employee code: Required, must be 4 digits
   * - Name: Required, must be Japanese characters
   * - User Type: Required
   * - Password: Required, minimum 6 characters, must contain uppercase and digit
   * - Password Confirmation: Must match password
   * 
   * Edit Mode:
   * - Employee ID: Disabled, not validated
   * - Name: Required, must be Japanese characters
   * - User Type: Required
   * - Password: Optional, but if provided must meet complexity requirements
   * - Password Confirmation: Must match password if password is provided
   * 
   * @returns {boolean} True if form is valid, false otherwise
   * 
   * @example
   * ```typescript
   * if (validateForm()) {
   *   // Proceed with form submission
   * }
   * ```
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<UserData> = {};

    if (mode === 'add') {
      if (!formData.empId) newErrors.empId = '従業員コードは必須です';
      else if (!isFourDigitEmployeeCode(formData.empId)) newErrors.empId = '従業員コードは4桁の数字で入力してください';

      if (!formData.name) newErrors.name = '名前は必須です';
      else if (!isJapaneseName(formData.name)) newErrors.name = '名前は漢字・ひらがな・カタカナのみ使用できます';

      if (!formData.userType) newErrors.userType = 'ユーザータイプは必須です';
      if (!formData.password) newErrors.password = 'パスワードは必須です';
      else if (formData.password.length < 6) newErrors.password = 'パスワードは6文字以上である必要があります';
      else if (!isPasswordValid(formData.password)) newErrors.password = 'パスワードは英数字のみで、少なくとも1つの大文字と数字を含めてください';
      if (formData.password !== formData.passwordConfirm) {
        newErrors.passwordConfirm = 'パスワードが一致しません';
      }
    } else {
      // edit mode: empId disabled; name and userType required; password fields optional
      if (!formData.name) newErrors.name = '名前は必須です';
      else if (!isJapaneseName(formData.name)) newErrors.name = '名前は漢字・ひらがな・カタカナのみ使用できます';

      if (!formData.userType) newErrors.userType = 'ユーザータイプは必須です';
      const passwordProvided = formData.password.trim().length > 0 || formData.passwordConfirm.trim().length > 0;
      if (passwordProvided) {
        if (!formData.password) newErrors.password = '新しいパスワードを入力してください';
        else if (formData.password.length < 6) newErrors.password = 'パスワードは6文字以上である必要があります';
        else if (!isPasswordValid(formData.password)) newErrors.password = 'パスワードは英数字のみで、少なくとも1つの大文字と数字を含めてください';
        if (formData.password !== formData.passwordConfirm) {
          newErrors.passwordConfirm = 'パスワードが一致しません';
        }
      }
    }

    if (formData.userType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN) {
      if (genbasLoading) {
        newErrors.assignedGenbaName = '現場一覧を読み込み中です。しばらくお待ちください。';
      } else if (genbasError) {
        newErrors.assignedGenbaName = genbasError;
      } else if (availableGenbas.length === 0) {
        newErrors.assignedGenbaName = '建設給与データに現場がありません。先にデータを登録してください。';
      } else if (!formData.assignedGenbaName?.trim()) {
        newErrors.assignedGenbaName = '担当現場を選択してください';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission.
   * 
   * Validates the form, then calls the appropriate callback (onAddUser or onUpdateUser)
   * based on the current mode. Clears form and closes modal on success.
   * 
   * @param e - Form submit event
   * 
   * @example
   * ```typescript
   * // Form submit handler
   * <form onSubmit={handleSubmit}>
   * ```
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (mode === 'add' && onAddUser) {
      onAddUser(formData);
      handleClear();
      onClose();
    } else if (mode === 'edit' && onUpdateUser) {
      onUpdateUser(formData);
      onClose();
    }
  };

  /**
   * Clears the form data and errors.
   * 
   * In edit mode, resets to initial data values.
   * In add mode, resets to default empty values.
   * 
   * @function
   * 
   * @example
   * ```typescript
   * // Clear form
   * handleClear();
   * ```
   */
  const handleClear = () => {
    if (mode === 'edit') {
      setFormData({
        empId: initialData?.empId ?? '',
        name: initialData?.name ?? '',
        userType: initialData?.userType ?? '従業員',
        password: '',
        passwordConfirm: '',
        currentPassword: '',
        assignedGenbaName: initialData?.assignedGenbaName ?? '',
      });
    } else {
      setFormData({
        empId: '',
        name: '',
        userType: '従業員',
        password: '',
        passwordConfirm: '',
        currentPassword: '',
        assignedGenbaName: '',
      });
    }
    setErrors({});
  };

  /**
   * Handles cancel action.
   * 
   * Clears form and closes modal.
   * 
   * @function
   * 
   * @example
   * ```typescript
   * // Cancel button handler
   * <button onClick={handleCancel}>Cancel</button>
   * ```
   */
  const handleCancel = () => {
    handleClear();
    onClose();
  };

  /**
   * Handles input field changes.
   * 
   * Updates form data and clears error for the changed field when user starts typing.
   * 
   * @param field - The field name to update
   * @param value - The new value for the field
   * 
   * @example
   * ```typescript
   * // Input change handler
   * <input onChange={(e) => handleInputChange('name', e.target.value)} />
   * ```
   */
  const handleInputChange = (field: keyof UserData, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'userType' && value !== OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN) {
        next.assignedGenbaName = '';
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (field === 'assignedGenbaName' && errors.assignedGenbaName) {
      setErrors((prev) => ({ ...prev, assignedGenbaName: undefined }));
    }
  };

  /**
   * Lock body scroll when modal is open.
   * 
   * Prevents background scrolling when modal is displayed.
   * Restores original overflow style when modal closes.
   * 
   * @effect
   * Runs when isOpen changes.
   */
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow when modal closes
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  /**
   * Don't render anything if modal is closed.
   */
  if (!isOpen) {
    return null;
  }

  /**
   * Render modal using portal to document.body.
   * 
   * This prevents parent container clipping and ensures modal
   * is always on top with proper z-index.
   */
  return createPortal(
    <div 
      className="modal-overlay" 
      style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}
    >
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <h3>{mode === 'edit' ? '従業員編集' : '新規従業員追加'}</h3>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="add-user-form">
          <div className="form-group">
            <label htmlFor="empId">従業員コード *</label>
            <input
              type="text"
              id="empId"
              value={formData.empId}
              onChange={(e) => {
                const v = e.target.value;
                if (mode === 'add' && /^\d*$/.test(v) && v.length <= 4) {
                  handleInputChange('empId', v);
                } else if (mode === 'edit') {
                  handleInputChange('empId', v);
                } else if (mode === 'add' && v.length <= 4) {
                  handleInputChange('empId', v.replace(/\D/g, ''));
                }
              }}
              placeholder={mode === 'edit' ? '編集時は変更できません' : '4桁の数字を入力'}
              inputMode="numeric"
              maxLength={4}
              pattern="[0-9]{4}"
              title={mode === 'edit' ? '編集時は変更できません' : '4桁の数字を入力してください'}
              className={errors.empId ? 'error' : ''}
              disabled={mode === 'edit'}
            />
            {errors.empId && <span className="error-message">{errors.empId}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="name">名前 *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="フルネームを入力"
              title="漢字・ひらがな・カタカナのみ使用できます（給与データの氏名と一致させてください）"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="userType">ユーザータイプ *</label>
            <select
              id="userType"
              value={formData.userType}
              onChange={(e) => handleInputChange('userType', e.target.value)}
              className={errors.userType ? 'error' : ''}
            >
              <option value="従業員">従業員</option>
              <option value="管理者">管理者（上位権限）</option>
              <option value="現場管理者">管理者（現場権限）</option>
              <option value={OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN}>管理者（担当現場権限）</option>
            </select>
            {errors.userType && <span className="error-message">{errors.userType}</span>}
          </div>

          {formData.userType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN && (
            <div className="form-group">
              <label htmlFor="assignedGenbaName">担当現場 *</label>
              <select
                id="assignedGenbaName"
                value={formData.assignedGenbaName ?? ''}
                onChange={(e) => handleInputChange('assignedGenbaName', e.target.value)}
                disabled={genbasLoading || availableGenbas.length === 0}
                className={errors.assignedGenbaName ? 'error' : ''}
              >
                <option value="">現場を選択</option>
                {availableGenbas.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              {genbasLoading && <span className="add-user-field-hint">現場一覧を読み込み中…</span>}
              {errors.assignedGenbaName && (
                <span className="error-message">{errors.assignedGenbaName}</span>
              )}
            </div>
          )}

          {mode === 'edit' && (
            <div className="form-group">
              <label htmlFor="currentPassword">現在のパスワード（パスワードを変更する場合）</label>
              <p className="add-user-field-hint">管理者が自分のパスワードを変更する場合は必須です。</p>
              <input
                type="password"
                id="currentPassword"
                value={formData.currentPassword ?? ''}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                placeholder="パスワードを変更する場合は入力（再起動後も給与データを表示できます）"
                className={errors.currentPassword ? 'error' : ''}
              />
              {errors.currentPassword && <span className="error-message">{errors.currentPassword}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">パスワード *</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder={mode === 'edit' ? '新しいパスワード（任意）' : 'パスワードを入力'}
              pattern="[A-Za-z0-9]{6,}"
              title="英数字のみ。6文字以上で、少なくとも1つの大文字と数字を含む必要があります"
              className={errors.password ? 'error' : ''}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="passwordConfirm">パスワード再入力 *</label>
            <input
              type="password"
              id="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
              placeholder={mode === 'edit' ? '新しいパスワードを再入力（任意）' : 'パスワードを再入力'}
              className={errors.passwordConfirm ? 'error' : ''}
            />
            {errors.passwordConfirm && <span className="error-message">{errors.passwordConfirm}</span>}
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              {mode === 'edit' ? 'リセット' : 'クリア'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary">
              {mode === 'edit' ? '更新' : '追加'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body // Render directly to body, outside component tree
  );
};
